import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Building2,
  Calendar,
  Check,
  FolderKanban,
  History,
  MessageSquare,
  Monitor,
  Paperclip,
  Pencil,
  Receipt,
  Send,
  Trash2,
  UserCheck,
  UserMinus,
  UserPlus,
  Wrench,
  X,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { FileUploadZone } from "@/modules/media/components/FileUploadZone";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { AssignAssetModal } from "../components/AssignAssetModal";
import { AddMaintenanceModal } from "../components/AddMaintenanceModal";
import { ExpensesTabContent } from "@/modules/expenses/components/ExpensesTabContent";
import {
  ConditionBadge,
  MaintenanceTypeBadge,
  StatusBadge,
  categoryColor,
  categoryIcon,
  formatCurrency,
  warrantyCell,
} from "../components/assetBadges";
import {
  useAddAssetComment,
  useAsset,
  useAssetCategories,
  useAssetComments,
  useAssetConditions,
  useAssetLocations,
  useDeleteAsset,
  useDeleteAssetComment,
  useReturnAsset,
  useUpdateAsset,
} from "../hooks/useAssets";
import type { AssetAssignment, AssetDetail } from "../types";

const BORDER = "hsl(210,30%,85%)";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function timeAgo(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function daysBetween(a: string, b: string | null): number {
  const start = new Date(a).getTime();
  const end = b ? new Date(b).getTime() : Date.now();
  return Math.max(0, Math.round((end - start) / (24 * 3600 * 1000)));
}

function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

type Tab = "history" | "maintenance" | "comments" | "expenses";

export default function AssetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: asset, isLoading } = useAsset(id ?? "");
  const updateMutation = useUpdateAsset(id ?? "");
  const deleteMutation = useDeleteAsset();
  const returnMutation = useReturnAsset(id ?? "");

  const { data: categories = [] } = useAssetCategories();
  const { data: conditions = [] } = useAssetConditions();
  const { data: locations = [] } = useAssetLocations();

  const commentsQuery = useAssetComments(id ?? "");
  const addComment = useAddAssetComment(id ?? "");
  const deleteComment = useDeleteAssetComment(id ?? "");

  const [editMode, setEditMode] = useState(false);
  const [tab, setTab] = useState<Tab>("history");
  const [assignOpen, setAssignOpen] = useState(false);
  const [maintOpen, setMaintOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [commentText, setCommentText] = useState("");

  // Edit fields
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editBrand, setEditBrand] = useState("");
  const [editModel, setEditModel] = useState("");
  const [editSerial, setEditSerial] = useState("");
  const [editAssetTag, setEditAssetTag] = useState("");
  const [editCondition, setEditCondition] = useState("good");
  const [editStatus, setEditStatus] = useState("available");
  const [editLocation, setEditLocation] = useState("");
  const [editLocationDetail, setEditLocationDetail] = useState("");
  const [editPurchaseDate, setEditPurchaseDate] = useState("");
  const [editPurchasePrice, setEditPurchasePrice] = useState("");
  const [editCurrency, setEditCurrency] = useState("AED");
  const [editCurrentValue, setEditCurrentValue] = useState("");
  const [editWarranty, setEditWarranty] = useState("");
  const [editInsuranceValue, setEditInsuranceValue] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editCompanyId, setEditCompanyId] = useState("");
  const [editProjectId, setEditProjectId] = useState("");

  const { data: companiesResult } = useCompanyList({
    page: 1,
    limit: 200,
    operationalOnly: true,
    bypassScope: true,
  });
  const companies = companiesResult?.data ?? [];
  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 200,
    companyId: editCompanyId || undefined,
  });
  const projects = projectsResult?.data ?? [];

  function enterEdit(a: AssetDetail) {
    setEditName(a.name);
    setEditDescription(a.description ?? "");
    setEditCategory(a.category);
    setEditBrand(a.brand ?? "");
    setEditModel(a.model ?? "");
    setEditSerial(a.serialNumber ?? "");
    setEditAssetTag(a.assetTag ?? "");
    setEditCondition(a.condition);
    setEditStatus(a.status);
    setEditLocation(a.location ?? "");
    setEditLocationDetail(a.locationDetail ?? "");
    setEditPurchaseDate(a.purchaseDate ? a.purchaseDate.slice(0, 10) : "");
    setEditPurchasePrice(a.purchasePrice?.toString() ?? "");
    setEditCurrency(a.purchaseCurrency ?? "AED");
    setEditCurrentValue(a.currentValue?.toString() ?? "");
    setEditWarranty(a.warrantyExpiry ? a.warrantyExpiry.slice(0, 10) : "");
    setEditInsuranceValue(a.insuranceValue?.toString() ?? "");
    setEditNotes(a.notes ?? "");
    setEditCompanyId(a.companyId ?? "");
    setEditProjectId(a.projectId ?? "");
    setEditMode(true);
  }

  function cancelEdit() {
    setEditMode(false);
  }

  async function saveEdit() {
    await updateMutation.mutateAsync({
      name: editName.trim(),
      description: editDescription.trim() || null,
      category: editCategory,
      brand: editBrand.trim() || null,
      model: editModel.trim() || null,
      serialNumber: editSerial.trim() || null,
      assetTag: editAssetTag.trim() || null,
      condition: editCondition,
      status: editStatus,
      location: editLocation || null,
      locationDetail: editLocationDetail.trim() || null,
      purchaseDate: editPurchaseDate
        ? new Date(editPurchaseDate).toISOString()
        : null,
      purchasePrice: editPurchasePrice ? Number(editPurchasePrice) : null,
      purchaseCurrency: editCurrency,
      currentValue: editCurrentValue ? Number(editCurrentValue) : null,
      warrantyExpiry: editWarranty
        ? new Date(editWarranty).toISOString()
        : null,
      insuranceValue: editInsuranceValue ? Number(editInsuranceValue) : null,
      notes: editNotes.trim() || null,
      companyId: editCompanyId || null,
      projectId: editProjectId || null,
    });
    setEditMode(false);
  }

  function handleDelete() {
    if (!asset) return;
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    if (!asset) return;
    await deleteMutation.mutateAsync(asset.id);
    navigate("/assets");
  }

  async function handleReturn() {
    if (!asset) return;
    const notes = window.prompt("Return notes (optional):") ?? undefined;
    await returnMutation.mutateAsync(notes);
  }

  async function submitComment() {
    if (!commentText.trim()) return;
    await addComment.mutateAsync(commentText.trim());
    setCommentText("");
  }

  const warrantyInfo = useMemo(
    () => warrantyCell(asset?.warrantyExpiry ?? null),
    [asset?.warrantyExpiry],
  );

  if (isLoading || !asset) {
    return (
      <AppLayout
        breadcrumb={[
          { label: "PhazeX Hub", href: "/" },
          { label: "Resources" },
          { label: "Assets", href: "/assets" },
        ]}
      >
        <div className="py-12 text-center text-muted-foreground">
          {isLoading ? "Loading…" : "Asset not found"}
        </div>
      </AppLayout>
    );
  }

  const Icon = categoryIcon(asset.category);
  const iconColor = categoryColor(asset.category);

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Resources" },
        { label: "Assets", href: "/assets" },
        { label: asset.code },
      ]}
    >
      {/* Page Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/assets")}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="Back"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <Monitor
            className="h-[18px] w-[18px] text-foreground"
            strokeWidth={1.5}
          />
          <h1 className="text-xl font-semibold text-foreground">View Asset</h1>
        </div>
        <div className="flex items-center gap-2">
          {asset.status === "available" && (
            <button
              type="button"
              onClick={() => setAssignOpen(true)}
              className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
              title="Assign"
            >
              <UserPlus
                className="h-4 w-4 text-[hsl(210,100%,40%)]"
                strokeWidth={1.5}
              />
            </button>
          )}
          {asset.status === "in_use" && (
            <button
              type="button"
              onClick={handleReturn}
              className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(0,70%,96%)]"
              title="Return"
            >
              <UserMinus
                className="h-4 w-4 text-[hsl(0,70%,45%)]"
                strokeWidth={1.5}
              />
            </button>
          )}
          <button
            type="button"
            onClick={() => setMaintOpen(true)}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="Maintenance"
          >
            <Wrench
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={() => (editMode ? cancelEdit() : enterEdit(asset))}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title={editMode ? "Exit edit mode" : "Edit"}
          >
            <Pencil
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(0,70%,96%)]"
            title="Delete"
          >
            <Trash2
              className="h-4 w-4 text-[hsl(0,70%,45%)]"
              strokeWidth={1.5}
            />
          </button>
        </div>
      </div>

      {/* ROW 1 — Summary */}
      <div
        className="mb-4 rounded-xl border bg-white"
        style={{ borderColor: BORDER, padding: "16px 20px" }}
      >
        <div className="flex items-start gap-4">
          <div
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
            style={{ background: `${iconColor}20`, color: iconColor }}
          >
            <Icon className="h-6 w-6" strokeWidth={1.5} />
          </div>
          <div className="min-w-0 flex-1">
            <h1
              style={{ fontSize: "20px", fontWeight: 700, margin: 0 }}
              className="leading-tight text-foreground"
            >
              {asset.name}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-muted-foreground">
              <ReferenceIdBadge id={asset.code} />
              <ConditionBadge value={asset.condition} />
              <StatusBadge value={asset.status} />
              {asset.brand || asset.model ? (
                <span>
                  {[asset.brand, asset.model].filter(Boolean).join(" ")}
                </span>
              ) : null}
              {asset.serialNumber && (
                <span className="font-mono">SN: {asset.serialNumber}</span>
              )}
            </div>

            {asset.currentAssignment && (
              <div
                className="mt-3 flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-[12px]"
                style={{
                  background: "hsl(210,55%,97%)",
                  borderColor: "hsl(210,55%,80%)",
                  color: "hsl(210,100%,30%)",
                }}
              >
                <UserCheck className="h-3.5 w-3.5" strokeWidth={1.5} />
                <span>
                  Assigned to{" "}
                  <strong>
                    {asset.currentAssignment.assignedToName ?? "—"}
                  </strong>
                  {asset.currentAssignment.projectName
                    ? ` for Project ${asset.currentAssignment.projectName}`
                    : ""}
                </span>
                <span>·</span>
                <span>
                  Since {formatDate(asset.currentAssignment.assignedAt)}
                </span>
                {asset.currentAssignment.expectedReturn && (
                  <>
                    <span>·</span>
                    <span>
                      Return by{" "}
                      {formatDate(asset.currentAssignment.expectedReturn)}
                    </span>
                  </>
                )}
              </div>
            )}

            {warrantyInfo.pill && (
              <div
                className="mt-2 flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-[12px]"
                style={{
                  background:
                    warrantyInfo.pill === "Expired"
                      ? "hsl(0,70%,97%)"
                      : "hsl(38,90%,97%)",
                  borderColor:
                    warrantyInfo.pill === "Expired"
                      ? "hsl(0,70%,85%)"
                      : "hsl(38,90%,80%)",
                  color:
                    warrantyInfo.pill === "Expired"
                      ? "hsl(0,70%,40%)"
                      : "hsl(35,90%,32%)",
                }}
              >
                <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} />
                <span>
                  {warrantyInfo.pill} · Warranty {warrantyInfo.text}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ROW 2 — Details + Financial/Assignment */}
      <div
        className="mb-4 grid gap-4"
        style={{ gridTemplateColumns: "60fr 40fr" }}
      >
        {/* LEFT */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER, padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center justify-between">
            <span style={{ fontSize: "13px", fontWeight: 600 }}>
              Asset Details
            </span>
            <div style={{ display: "flex", gap: "4px" }}>
              {editMode ? (
                <>
                  <button
                    type="button"
                    onClick={saveEdit}
                    disabled={updateMutation.isPending}
                    style={{
                      width: "28px",
                      height: "28px",
                      border: "1px solid hsl(173,50%,70%)",
                      borderRadius: "4px",
                      background: "hsl(173,50%,94%)",
                      color: "hsl(173,100%,25%)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title="Save"
                  >
                    <Check size={14} strokeWidth={2} />
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    style={{
                      width: "28px",
                      height: "28px",
                      border: "1px solid hsl(0,50%,80%)",
                      borderRadius: "4px",
                      background: "hsl(0,50%,96%)",
                      color: "hsl(0,84%,50%)",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    title="Cancel"
                  >
                    <X size={14} strokeWidth={2} />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => enterEdit(asset)}
                  style={{
                    width: "28px",
                    height: "28px",
                    border: "1px solid hsl(210,30%,85%)",
                    borderRadius: "4px",
                    background: "transparent",
                    color: "hsl(215,16%,47%)",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  title="Edit"
                >
                  <Pencil size={14} strokeWidth={1.5} />
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <Field label="Name">
              {editMode ? (
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              ) : (
                <span className="text-foreground">{asset.name}</span>
              )}
            </Field>
            <Field label="Code">
              <span className="font-mono text-foreground">{asset.code}</span>
            </Field>
            <Field label="Category">
              {editMode ? (
                <Select
                  value={editCategory}
                  onChange={(e) => setEditCategory(e.target.value)}
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              ) : (
                <span className="capitalize text-foreground">
                  {asset.category.replace(/_/g, " ")}
                </span>
              )}
            </Field>
            <Field label="Brand">
              {editMode ? (
                <Input
                  value={editBrand}
                  onChange={(e) => setEditBrand(e.target.value)}
                />
              ) : (
                <span className="text-foreground">{asset.brand ?? "—"}</span>
              )}
            </Field>
            <Field label="Model">
              {editMode ? (
                <Input
                  value={editModel}
                  onChange={(e) => setEditModel(e.target.value)}
                />
              ) : (
                <span className="text-foreground">{asset.model ?? "—"}</span>
              )}
            </Field>
            <Field label="Serial Number">
              {editMode ? (
                <Input
                  value={editSerial}
                  onChange={(e) => setEditSerial(e.target.value)}
                />
              ) : (
                <span className="font-mono text-foreground">
                  {asset.serialNumber ?? "—"}
                </span>
              )}
            </Field>
            <Field label="Asset Tag">
              {editMode ? (
                <Input
                  value={editAssetTag}
                  onChange={(e) => setEditAssetTag(e.target.value)}
                />
              ) : (
                <span className="font-mono text-foreground">
                  {asset.assetTag ?? "—"}
                </span>
              )}
            </Field>
            <Field label="Condition">
              {editMode ? (
                <Select
                  value={editCondition}
                  onChange={(e) => setEditCondition(e.target.value)}
                >
                  {conditions.map((c) => (
                    <option key={c.id} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              ) : (
                <ConditionBadge value={asset.condition} />
              )}
            </Field>
            <Field label="Status">
              {editMode ? (
                <Select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value)}
                >
                  <option value="available">Available</option>
                  <option value="in_use">In Use</option>
                  <option value="under_repair">Under Repair</option>
                  <option value="retired">Retired</option>
                  <option value="lost">Lost</option>
                </Select>
              ) : (
                <StatusBadge value={asset.status} />
              )}
            </Field>
            <Field label="Location">
              {editMode ? (
                <Select
                  value={editLocation}
                  onChange={(e) => setEditLocation(e.target.value)}
                >
                  <option value="">—</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.value}>
                      {l.label}
                    </option>
                  ))}
                </Select>
              ) : (
                <span className="capitalize text-foreground">
                  {asset.location?.replace(/_/g, " ") ?? "—"}
                </span>
              )}
            </Field>
            <Field label="Location Detail">
              {editMode ? (
                <Input
                  value={editLocationDetail}
                  onChange={(e) => setEditLocationDetail(e.target.value)}
                />
              ) : (
                <span className="text-foreground">
                  {asset.locationDetail ?? "—"}
                </span>
              )}
            </Field>
            <Field label="Company">
              {editMode ? (
                <Select
                  value={editCompanyId}
                  onChange={(e) => {
                    setEditCompanyId(e.target.value);
                    setEditProjectId("");
                  }}
                >
                  <option value="">— None —</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              ) : asset.companyId ? (
                <Link
                  to={`/companies/${asset.companyId}`}
                  className="text-primary hover:underline"
                >
                  {asset.companyName ?? "Company"}
                </Link>
              ) : (
                <span className="text-foreground">—</span>
              )}
            </Field>
            <Field label="Project">
              {editMode ? (
                <Select
                  value={editProjectId}
                  onChange={(e) => setEditProjectId(e.target.value)}
                >
                  <option value="">— None —</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </Select>
              ) : asset.projectId ? (
                <Link
                  to={`/projects/${asset.projectId}`}
                  className="text-primary hover:underline"
                >
                  {asset.project?.name ?? asset.projectName ?? "Project"}
                </Link>
              ) : (
                <span className="text-foreground">—</span>
              )}
            </Field>
          </div>

          {asset.specifications &&
            Object.keys(asset.specifications).length > 0 && (
              <div className="mt-4">
                <div className="text-[11px] uppercase text-muted-foreground">
                  Specifications
                </div>
                <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  {Object.entries(asset.specifications).map(([k, v]) => (
                    <div key={k} className="text-foreground">
                      <span className="text-muted-foreground capitalize">
                        {k}:
                      </span>{" "}
                      {String(v)}
                    </div>
                  ))}
                </div>
              </div>
            )}

          <div className="mt-4">
            <div className="text-[11px] uppercase text-muted-foreground">
              Description
            </div>
            {editMode ? (
              <textarea
                rows={2}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                style={{ borderColor: BORDER }}
              />
            ) : (
              <div className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                {asset.description?.trim() ? asset.description : "—"}
              </div>
            )}
          </div>

          <div className="mt-4">
            <div className="text-[11px] uppercase text-muted-foreground">
              Notes
            </div>
            {editMode ? (
              <textarea
                rows={2}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                style={{ borderColor: BORDER }}
              />
            ) : (
              <div className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                {asset.notes?.trim() ? asset.notes : "—"}
              </div>
            )}
          </div>

          {asset.tags.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5">
              {asset.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER, padding: "16px 20px" }}
        >
          <h3 className="mb-3 text-[13px] font-semibold text-foreground">
            Financial
          </h3>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <Field label="Purchase Date">
              {editMode ? (
                <Input
                  type="date"
                  value={editPurchaseDate}
                  onChange={(e) => setEditPurchaseDate(e.target.value)}
                />
              ) : (
                <span className="text-foreground">
                  {formatDate(asset.purchaseDate)}
                </span>
              )}
            </Field>
            <Field label="Purchase Price">
              {editMode ? (
                <Input
                  type="number"
                  step="0.01"
                  value={editPurchasePrice}
                  onChange={(e) => setEditPurchasePrice(e.target.value)}
                />
              ) : (
                <span className="text-foreground">
                  {formatCurrency(
                    asset.purchasePrice,
                    asset.purchaseCurrency,
                  )}
                </span>
              )}
            </Field>
            <Field label="Current Value">
              {editMode ? (
                <Input
                  type="number"
                  step="0.01"
                  value={editCurrentValue}
                  onChange={(e) => setEditCurrentValue(e.target.value)}
                />
              ) : (
                <span className="text-foreground">
                  {formatCurrency(
                    asset.currentValue,
                    asset.purchaseCurrency,
                  )}
                </span>
              )}
            </Field>
            <Field label="Warranty Expiry">
              {editMode ? (
                <Input
                  type="date"
                  value={editWarranty}
                  onChange={(e) => setEditWarranty(e.target.value)}
                />
              ) : (
                <span className="text-foreground">
                  {formatDate(asset.warrantyExpiry)}
                </span>
              )}
            </Field>
            <Field label="Insurance Value">
              {editMode ? (
                <Input
                  type="number"
                  step="0.01"
                  value={editInsuranceValue}
                  onChange={(e) => setEditInsuranceValue(e.target.value)}
                />
              ) : (
                <span className="text-foreground">
                  {formatCurrency(
                    asset.insuranceValue,
                    asset.purchaseCurrency,
                  )}
                </span>
              )}
            </Field>
            <Field label="Currency">
              {editMode ? (
                <Select
                  value={editCurrency}
                  onChange={(e) => setEditCurrency(e.target.value)}
                >
                  {["AED", "USD", "EUR", "GBP", "INR", "SAR"].map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              ) : (
                <span className="text-foreground">
                  {asset.purchaseCurrency ?? "AED"}
                </span>
              )}
            </Field>
          </div>

          <div
            className="my-4 border-t"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          />

          <h3 className="mb-2 text-[13px] font-semibold text-foreground">
            Current Assignment
          </h3>
          {asset.currentAssignment ? (
            <div className="space-y-1.5 text-sm">
              {asset.currentAssignment.assignedToName && (
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">Assigned to:</span>
                  <span className="text-foreground">
                    {asset.currentAssignment.assignedToName}
                  </span>
                </div>
              )}
              {asset.currentAssignment.projectId && (
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">For project:</span>
                  <Link
                    to={`/projects/${asset.currentAssignment.projectId}`}
                    className="text-primary hover:underline"
                  >
                    {asset.currentAssignment.projectName ?? "—"}
                  </Link>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <span className="text-muted-foreground">Assigned on:</span>
                <span className="text-foreground">
                  {formatDate(asset.currentAssignment.assignedAt)}
                </span>
              </div>
              {asset.currentAssignment.expectedReturn && (
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground">
                    Expected return:
                  </span>
                  <span
                    className="text-foreground"
                    style={{
                      color:
                        new Date(
                          asset.currentAssignment.expectedReturn,
                        ).getTime() < Date.now()
                          ? "hsl(0,70%,40%)"
                          : undefined,
                    }}
                  >
                    {formatDate(asset.currentAssignment.expectedReturn)}
                  </span>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleReturn}
                className="mt-2 text-[hsl(0,70%,45%)]"
              >
                <UserMinus className="h-3.5 w-3.5" strokeWidth={1.5} />
                Return Asset
              </Button>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="text-muted-foreground">
                Available — not currently assigned
              </div>
              <Button size="sm" onClick={() => setAssignOpen(true)}>
                <UserPlus className="h-3.5 w-3.5" strokeWidth={1.5} />
                Assign Asset
              </Button>
            </div>
          )}

          <div
            className="my-4 border-t"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          />

          <h3 className="mb-2 text-[13px] font-semibold text-foreground">
            Location
          </h3>
          <div className="space-y-1.5 text-sm">
            {asset.location ? (
              <div className="flex items-center gap-1.5">
                <span
                  className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize"
                  style={{
                    background: "hsl(210,16%,93%)",
                    color: "hsl(215,16%,40%)",
                  }}
                >
                  {asset.location.replace(/_/g, " ")}
                </span>
                {asset.locationDetail && (
                  <span className="text-muted-foreground">
                    · {asset.locationDetail}
                  </span>
                )}
              </div>
            ) : (
              <div className="text-muted-foreground">—</div>
            )}
            {asset.companyId && (
              <div className="flex items-center gap-1.5">
                <Building2
                  className="h-3 w-3 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Link
                  to={`/companies/${asset.companyId}`}
                  className="text-primary hover:underline"
                >
                  {asset.companyName ?? "Company"}
                </Link>
              </div>
            )}
            {asset.projectId && (
              <div className="flex items-center gap-1.5">
                <FolderKanban
                  className="h-3 w-3 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <Link
                  to={`/projects/${asset.projectId}`}
                  className="text-primary hover:underline"
                >
                  {asset.project?.name ?? asset.projectName ?? "Project"}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ROW 3 — Tabs */}
      <div
        className="mb-4 overflow-hidden rounded-xl border bg-white"
        style={{ borderColor: BORDER }}
      >
        <div
          className="flex items-center border-b"
          style={{ borderColor: "hsl(210,30%,90%)" }}
        >
          <TabBtn
            active={tab === "history"}
            onClick={() => setTab("history")}
            label="Assignment History"
            count={asset.assignmentHistory.length}
            icon={History}
          />
          <TabBtn
            active={tab === "maintenance"}
            onClick={() => setTab("maintenance")}
            label="Maintenance"
            count={asset.maintenanceRecords.length}
            icon={Wrench}
          />
          <TabBtn
            active={tab === "comments"}
            onClick={() => setTab("comments")}
            label="Comments"
            count={commentsQuery.data?.length ?? 0}
            icon={MessageSquare}
          />
          <TabBtn
            active={tab === "expenses"}
            onClick={() => setTab("expenses")}
            label="Related Expenses"
            count={asset.expensesCount}
            icon={Receipt}
          />
        </div>

        <div className="p-4">
          {tab === "history" && (
            <HistoryTable history={asset.assignmentHistory} />
          )}

          {tab === "maintenance" && (
            <div>
              <div className="mb-3 flex items-center justify-between">
                <div className="text-[12px] text-muted-foreground">
                  All maintenance records
                </div>
                <Button size="sm" onClick={() => setMaintOpen(true)}>
                  <Wrench className="h-3.5 w-3.5" strokeWidth={1.5} />
                  Add Maintenance
                </Button>
              </div>
              {asset.maintenanceRecords.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  No maintenance records
                </div>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead
                    className="border-b text-[11px] font-semibold uppercase text-muted-foreground"
                    style={{ borderColor: "hsl(210,30%,90%)" }}
                  >
                    <tr>
                      <th className="px-2 py-2">Date</th>
                      <th className="px-2 py-2">Type</th>
                      <th className="px-2 py-2">Description</th>
                      <th className="px-2 py-2">Performed By</th>
                      <th className="px-2 py-2">Cost</th>
                      <th className="px-2 py-2">Next Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {asset.maintenanceRecords.map((m) => (
                      <tr
                        key={m.id}
                        className="border-b"
                        style={{ borderColor: "hsl(210,30%,92%)" }}
                      >
                        <td className="px-2 py-2">
                          {formatDate(m.maintenanceDate)}
                        </td>
                        <td className="px-2 py-2">
                          <MaintenanceTypeBadge value={m.type} />
                        </td>
                        <td className="px-2 py-2">{m.description}</td>
                        <td className="px-2 py-2 text-muted-foreground">
                          {m.performedBy ?? "—"}
                        </td>
                        <td className="px-2 py-2">
                          {formatCurrency(m.cost, m.currency)}
                        </td>
                        <td className="px-2 py-2 text-muted-foreground">
                          {formatDate(m.nextDueDate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {tab === "expenses" && asset && (
            <ExpensesTabContent assetId={asset.id} />
          )}

          {tab === "comments" && (
            <div className="flex flex-col">
              <div className="max-h-[300px] overflow-y-auto">
                {(!commentsQuery.data || commentsQuery.data.length === 0) && (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No comments yet.
                  </div>
                )}
                {[...(commentsQuery.data ?? [])].reverse().map((c) => {
                  const isOwn = c.authorId === user?.id;
                  return (
                    <div
                      key={c.id}
                      className="border-b py-3 last:border-b-0"
                      style={{ borderColor: "hsl(210,30%,93%)" }}
                    >
                      <div className="flex items-start gap-2">
                        <span
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                          style={{
                            background: "hsl(210,55%,92%)",
                            color: "hsl(210,100%,28%)",
                          }}
                        >
                          {initialsOf(c.authorName ?? "??")}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[13px] font-semibold text-foreground">
                              {c.authorName ?? "Unknown"}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {timeAgo(c.createdAt)}
                            </span>
                            {isOwn && (
                              <button
                                type="button"
                                onClick={() => deleteComment.mutate(c.id)}
                                className="ml-auto text-muted-foreground hover:text-[hsl(0,70%,45%)]"
                                title="Delete"
                              >
                                <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                              </button>
                            )}
                          </div>
                          <div className="mt-1 whitespace-pre-wrap text-[14px] text-foreground">
                            {c.content}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                className="mt-3 flex items-end gap-2 border-t pt-3"
                style={{ borderColor: "hsl(210,30%,90%)" }}
              >
                <textarea
                  rows={2}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.ctrlKey && e.key === "Enter") {
                      e.preventDefault();
                      submitComment();
                    }
                  }}
                  placeholder="Write a comment… (Ctrl+Enter to submit)"
                  className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
                  style={{ borderColor: BORDER }}
                />
                <button
                  type="button"
                  onClick={submitComment}
                  disabled={!commentText.trim() || addComment.isPending}
                  className="flex h-9 w-9 items-center justify-center rounded-full text-white disabled:opacity-50"
                  style={{ background: "hsl(210,100%,45%)" }}
                  title="Send"
                >
                  <Send className="h-3.5 w-3.5" strokeWidth={1.8} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ROW 4 — Attachments + meta */}
      <div
        className="mb-4 grid gap-4"
        style={{ gridTemplateColumns: "60fr 40fr" }}
      >
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER, padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center gap-2">
            <Calendar
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
            <h3 className="text-[13px] font-semibold text-foreground">
              Timestamps
            </h3>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
            <Field label="Created At">
              <span className="text-foreground">
                {formatDateTime(asset.createdAt)}
              </span>
            </Field>
            <Field label="Updated At">
              <span className="text-foreground">
                {formatDateTime(asset.updatedAt)}
              </span>
            </Field>
            <Field label="Created By">
              <span className="text-foreground">{asset.createdBy ?? "—"}</span>
            </Field>
            <Field label="Updated By">
              <span className="text-foreground">{asset.updatedBy ?? "—"}</span>
            </Field>
          </div>
        </div>

        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: BORDER, padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center gap-2">
            <Paperclip
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
            <h3 className="text-[13px] font-semibold text-foreground">
              Attachments
            </h3>
          </div>
          <FileUploadZone
            entityType="asset"
            entityId={asset.id}
            entityLabel={asset.code}
            companyId={asset.companyId ?? undefined}
            projectId={asset.projectId ?? undefined}
            showExisting
            compact
          />
        </div>
      </div>

      <AssignAssetModal
        isOpen={assignOpen}
        onClose={() => setAssignOpen(false)}
        assetId={asset.id}
        assetName={asset.name}
      />
      <AddMaintenanceModal
        isOpen={maintOpen}
        onClose={() => setMaintOpen(false)}
        assetId={asset.id}
        assetName={asset.name}
        currentStatus={asset.status}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete asset?"
        description={`Are you sure you want to delete '${asset.name}'? It will be removed from your Assets list.`}
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase text-muted-foreground">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  label,
  count,
  icon: Icon,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  icon: typeof MessageSquare;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
        active
          ? "border-primary text-primary"
          : "border-transparent text-muted-foreground"
      }`}
    >
      <Icon className="h-4 w-4" strokeWidth={1.5} />
      {label}
      <span
        className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium"
        style={{
          background: active ? "hsl(210,55%,92%)" : "hsl(210,16%,93%)",
          color: active ? "hsl(210,100%,28%)" : "hsl(215,16%,40%)",
        }}
      >
        {count}
      </span>
    </button>
  );
}

function HistoryTable({ history }: { history: AssetAssignment[] }) {
  if (history.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-muted-foreground">
        No assignment history
      </div>
    );
  }
  return (
    <table className="w-full text-left text-sm">
      <thead
        className="border-b text-[11px] font-semibold uppercase text-muted-foreground"
        style={{ borderColor: "hsl(210,30%,90%)" }}
      >
        <tr>
          <th className="px-2 py-2">Assigned To</th>
          <th className="px-2 py-2">Project</th>
          <th className="px-2 py-2">Assigned</th>
          <th className="px-2 py-2">Expected Return</th>
          <th className="px-2 py-2">Returned</th>
          <th className="px-2 py-2">Duration</th>
          <th className="px-2 py-2">Notes</th>
        </tr>
      </thead>
      <tbody>
        {history.map((h) => {
          const current = h.returnedAt === null;
          return (
            <tr
              key={h.id}
              className="border-b"
              style={{
                borderColor: "hsl(210,30%,92%)",
                background: current ? "hsl(210,55%,98%)" : undefined,
              }}
            >
              <td className="px-2 py-2">{h.assignedToName ?? "—"}</td>
              <td className="px-2 py-2 text-muted-foreground">
                {h.projectName ?? "—"}
              </td>
              <td className="px-2 py-2">
                {new Date(h.assignedAt).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </td>
              <td className="px-2 py-2 text-muted-foreground">
                {h.expectedReturn
                  ? new Date(h.expectedReturn).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "—"}
              </td>
              <td className="px-2 py-2">
                {current ? (
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
                    style={{
                      background: "hsl(210,55%,92%)",
                      color: "hsl(210,100%,28%)",
                    }}
                  >
                    Currently assigned
                  </span>
                ) : (
                  <span style={{ color: "hsl(142,72%,28%)" }}>
                    {new Date(h.returnedAt!).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                )}
              </td>
              <td className="px-2 py-2 text-muted-foreground">
                {daysBetween(h.assignedAt, h.returnedAt)} days
              </td>
              <td className="px-2 py-2 text-muted-foreground text-xs">
                {h.notes ?? "—"}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
